import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { useEffect, useRef } from "react";

import { useShellRevealStore } from "./stores/useShellRevealStore";
import { useIsCircleAiPageRoute } from "./useIsCircleAiPageRoute";
import { useSignUpQuestionsPending } from "./useSignUpQuestionsPending";

const REVEAL_DELAY_MS = 2500;
const REVEAL_ANIM_MS = 1200;

export const useShellRevealEffect = () => {
  const { isAiRoute } = useIsCircleAiPageRoute();
  const isPending = useSignUpQuestionsPending();
  const wasPending = useRef(isPending);
  const phase = useShellRevealStore(s => s.phase);
  const setPhase = useShellRevealStore(s => s.setPhase);

  useEffect(() => {
    // Only reveal when questions were just answered on the AI route — not on
    // navigation away (which also makes isPending false via isAiRoute=false).
    if (wasPending.current && !isPending && isAiRoute) {
      setPhase("holding");
      const shellStore = useCircleAIShellStore.getState();
      if (!shellStore.isDockManualOverride) {
        shellStore.setDockMode("expanded");
      }
    }
    wasPending.current = isPending;
  }, [isPending, isAiRoute, setPhase]);

  useEffect(() => {
    if (phase === "holding") {
      const timer = setTimeout(() => setPhase("revealing"), REVEAL_DELAY_MS);
      return () => clearTimeout(timer);
    }
    if (phase === "revealing") {
      const timer = setTimeout(() => setPhase("idle"), REVEAL_ANIM_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [phase, setPhase]);
};

export type ShellVisibility = "hidden" | "revealing" | "visible";

export const useShellReveal = (): ShellVisibility => {
  const isPending = useSignUpQuestionsPending();
  const phase = useShellRevealStore(s => s.phase);

  if (isPending || phase === "holding") return "hidden";
  if (phase === "revealing") return "revealing";
  return "visible";
};
